import {Component, inject, OnInit, signal, ViewChild, WritableSignal} from '@angular/core';
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {
  CustomTableComponent, AccessRole, ButtonColumnType, FormGroupErrorMessageComponent,
  CustomTitle, ICustomTable, DataColumnType, ISettings, LocalService
} from "lib-base";
import {firstValueFrom, forkJoin} from "rxjs";
import {ControlTiempoService} from "../../../services/database/control-tiempo.service";
import {
  IControlTiempo,
  ICorreoPlantillaEditDto,
  IRangoTiempo,
  ITipoPlazo,
} from "../../../shared/interfaces/database/configuracion";
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {DialogModule} from "primeng/dialog";
import {InputTextModule} from "primeng/inputtext";
import {InputNumberModule} from "primeng/inputnumber";
import {ButtonDirective} from "primeng/button";
import {LoadingPanelService} from "../../../../../../lib-base/src/lib";
import {ConfirmationService, MessageService} from "primeng/api";
import {Event} from "@angular/router";
import {TableModule} from "primeng/table";
import {RangoTiempoComponent} from "../rango-tiempo/rango-tiempo.component";
import {DropdownModule} from "primeng/dropdown";
import {TipoPlazoService} from "../../../services/database/tipoPlazo.service";
import {InputTextareaModule} from "primeng/inputtextarea";
import {CustomTitleDirective} from "../../../shared/clases/custom-title.directive";

@Component({
  selector: 'app-control-tiempo',
  standalone: true,
  imports: [
    TranslatePipe, CustomTableComponent, CustomTitle, FormGroupErrorMessageComponent,
    DialogModule, ReactiveFormsModule, InputTextModule, InputNumberModule,
    ButtonDirective, TableModule, RangoTiempoComponent, DropdownModule, InputTextareaModule
  ],
  templateUrl: './control-tiempo.component.html',
  styleUrl: './control-tiempo.component.scss'
})
export class ControlTiempoComponent implements OnInit {

  configTable!: ICustomTable;
  translations: any;
  role = AccessRole;
  form!: FormGroup;
  openDialog: boolean = false;
  title: WritableSignal<string> = signal<string>('');
  private id:number | undefined = undefined;
  tiposPlazos:ITipoPlazo[] = [];
  private titleService:CustomTitleDirective = inject(CustomTitleDirective);

  @ViewChild(CustomTableComponent) customTableComponent !: CustomTableComponent;

  private _t: TranslateService = inject(TranslateService);
  private readonly _lps: LoadingPanelService = inject(LoadingPanelService);
  private _controlTiempoService: ControlTiempoService = inject(ControlTiempoService);
  protected readonly ls: LocalService = inject(LocalService);
  private messageService: MessageService = inject(MessageService);
  private tipoPlazoService: TipoPlazoService = inject(TipoPlazoService);
  private readonly _cs: ConfirmationService = inject(ConfirmationService);

  constructor() {
    this.initForm();
  }

  ngOnInit(): void {
    this.initTranslations()
      .then(() => this.initTable());
    this.getTiposPlazos();
    this.titleService.changeTitle('components.app-control-tiempo.title');
  }

  private async initTranslations() {
    this.translations = await firstValueFrom(forkJoin({
      id: this._t.get('components.app-control-tiempo.id'),
      descripcion: this._t.get('components.app-control-tiempo.descripcion'),
      nombre: this._t.get('components.app-control-tiempo.nombre'),
      tipoPlazo: this._t.get('components.app-control-tiempo.tipo-plazo'),
      success: this._t.get('common.success'),
      estado: this._t.get('components.app-control-tiempo.estado'),
      summaryWarn: this._t.get('form.msg.warn-summary'),
      controlCreado: this._t.get('components.app-control-tiempo.control-creado'),
      controlActualizado: this._t.get('components.app-control-tiempo.control-actualizado'),
      titleActualizarControl: this._t.get('components.app-control-tiempo.title-actualizar'),
      controlActivado: this._t.get('components.app-control-tiempo.control-activado'),
      controlEliminado: this._t.get('components.app-control-tiempo.control-eliminado'),
      titleCrearControl: this._t.get('components.app-control-tiempo.title-crear'),
      estaSeguroEliminar: this._t.get('components.app-control-tiempo.msg-desactivar'),
    }))
  }

  private initTable() {
    this.configTable = {
      columns: [
        {
          field: 'expand',
          header: '',
          class:'p-button-info',
          width: '0.5rem',
          buttonType: ButtonColumnType.EXPANDED
        },
        {
          field: 'id',
          header: this.translations.id,
          class: 'font-13',
          width: '8em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          columnFilter: true,
          dataType: DataColumnType.NUMBER
        },
        {
          field: 'nombre',
          header: this.translations.nombre,
          class: 'font-13',
          width: '15em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: false,
          columnFilter: false,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'descripcion',
          header: this.translations.descripcion,
          class: 'font-13',
          width: '22em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: false,
          columnFilter: false,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'tipoPlazoDescripcion',
          header: this.translations.tipoPlazo,
          class: 'font-13',
          width: '10em',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          columnFilter: true,
          dataType: DataColumnType.TEXT
        },
        {
          field: 'estado',
          header: this.translations.estado,
          class: 'button-edit p-button-outlined',
          width: '6rem',
          columnmHeader: {
            class: 'text-left color-blue-2 font-13 font-bold',
            style: null
          },
          sort: true,
          columnFilter: true,
          buttonType: ButtonColumnType.CHECK,
          onClick: (event: Event, data: Required<Omit<IControlTiempo,'rangos'>>) =>
            this.desactivarControl(event, data),
        },
         {
          field: 'edit',
          header: '',
          width: '0rem',
          class: 'pt-2 pb-2 pr-1',
          buttonType: ButtonColumnType.EDIT,
          disabled: !this.ls.hasRole(this.role.ADMIN_SEC),
          tooltip: {
            message: this.translations.requiereRolSeguridad
          },
          onClick: (event:Event, data: IControlTiempo) =>
            this.editRegistry(data),
          headerBtn: {
            icon: 'pi pi-plus',
            class: 'pt-2 pb-2 pl-0 pr-0',
            onClick: () => this.newRegister(),
          }
        }
      ],
      settings: {
        table: {
          rows: 10,
          lazy: true
        }
      } as ISettings
    } as ICustomTable;
  }

  private initForm() {
    this.form = new FormGroup({
      descripcion: new FormControl(null, Validators.required),
      nombre: new FormControl(null, Validators.required),
      tipoPlazo: new FormControl(null, Validators.required),
    })
  }

  getRecords($event?: any) {
    this._controlTiempoService.getAllControls($event ?? {}).subscribe({
      next: value => {
        if (this.customTableComponent) {
          this.customTableComponent.data = value.content;
          this.customTableComponent.totalRecords = value.totalElements;
        }
      }
    });
  }

  hideModal(): void {
    this.form.reset();
    this.id = undefined;
  }

  editRegistry(data: IControlTiempo) {
    this.title.set(this.translations.titleActualizarControl);
    this.id = data.id;
    this.form.setValue({
      nombre:data.nombre,
      descripcion:data.descripcion,
      tipoPlazo: data.tipoPlazo
    });
    this.openDialog = true;
  }

  newRegister() {
    this.title.set(this.translations.titleCrearControl);
    this.openDialog = true;
  }

  getBody(): Omit<IControlTiempo, 'id'> {
    const body: Omit<IControlTiempo, 'id'> = {
      descripcion: this.form.get('descripcion')?.value,
      tipoPlazo: this.form.get('tipoPlazo')?.value,
      nombre: this.form.get('nombre')?.value
    };
    return body;
  }

  onSubmit() {
    this._lps.show();
    const body = this.getBody();
    if (!this.id){
      this._controlTiempoService.crear(body).subscribe({
        next: value => {
          this.getContent(this.translations.controlCreado);
        }
      });
    }else {
      this._controlTiempoService.actualizar(this.id,body).subscribe({
        next: value => {
         this.getContent(this.translations.controlActualizado);
        }
      });
    }
  }

  private getContent = (message: string) => {
    this.getRecords();
    this.messageService.add({
      severity: 'success',
      summary: this.translations.success,
      detail: message
    });
    this._lps.hide();
    this.openDialog = false;
    this.hideModal();
  }

  desactivarControl(event: any,data :Required<Omit<IControlTiempo,'rangos'>>) {
    if (data.estado) {
      this._cs.confirm({
        target: event.target as EventTarget,
        message: this.translations.estaSeguroEliminar,
        icon: 'pi pi-exclamation-triangle',
        accept: () => this.desactivar(data.id, this.translations.controlEliminado)
      });
    } else {
      this.desactivar(data.id, this.translations.controlActivado);
    }

  }

  private desactivar(id:number, message:string){
    this._lps.show(true);
    this._controlTiempoService.desactivar(id).subscribe({
      next: () => {
        this.getContent(message);
      }
    });
  }
  
  private getTiposPlazos() {
    this.tipoPlazoService.getTipoPlazos().subscribe({
      next: value => {
        this.tiposPlazos = value;
      }
    })
  }
}
